import {
	mdiClockOutline,
	mdiClose,
	mdiDotsVertical,
	mdiDownload,
	mdiMagnifyMinus,
	mdiMagnifyPlus,
	mdiRefresh,
	mdiTranslate,
} from '@mdi/js';
import Icon from '@mdi/react';
import {
	Backdrop,
	Box,
	Button,
	Fade,
	IconButton,
	Menu,
	MenuItem,
	Modal,
	styled,
	Tooltip,
	Typography,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Attachment } from 'services/chat';
import { useLanguages, useTranslate } from 'services/translate/useTranslate';
import { MessageSendStatus, MessageStatus } from '../types';
import { Message } from '../types/message';
import { MessageContent, MessageContentType } from '../types/messageContent';
import { FileAttachment } from './components/FileAttachment';
import { MediaImage, MediaVideo, MEDIA_SIZES, VoiceMessage } from './components/MediaComponents';
import { formatTimestamp, getFullUrl, isVoiceMessage, RichTextContent } from './utils/messageUtils';

// Interface definitions
interface MessageBubbleProps {
	message: Message;
	isCurrentUser: boolean;
	onEdit?: (message: Message) => void;
	onDelete?: (message: Message) => void;
	onRetry?: (message: Message) => Promise<void>;
}

interface MessageWithRetry extends Message {
	onRetry?: () => Promise<void>;
}

// Styled components
const MessageBubbleComponent = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'isCurrentUser',
})<{ isCurrentUser: boolean }>(({ isCurrentUser }) => ({
	maxWidth: '70%',
	padding: '6px 10px',
	borderRadius: '12px',
	backgroundColor: isCurrentUser ? '#FFF4EA' : 'white',
	marginLeft: isCurrentUser ? 'auto' : '0',
	marginRight: isCurrentUser ? '0' : 'auto',
	position: 'relative',
	boxShadow: '0 1px 1px rgba(0,0,0,0.1)',
	alignSelf: isCurrentUser ? 'flex-end' : 'flex-start',
	display: 'inline-block',
	minHeight: 'auto',
}));

const TimeStamp = styled(Typography)({
	fontSize: '0.45rem !important',
	color: '#667781',
	marginLeft: '8px',
	float: 'right',
});

export const MessageBubble = React.memo<MessageBubbleProps>(
	({ message, isCurrentUser, onEdit, onDelete, onRetry }) => {
		// State for menus and modals
		const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
		const [contextMenuAnchorEl, setContextMenuAnchorEl] = useState<null | HTMLElement>(null);
		const [languageMenuAnchorEl, setLanguageMenuAnchorEl] = useState<null | HTMLElement>(null);
		const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
		const [currentMediaUrl, setCurrentMediaUrl] = useState<string>('');
		const [currentMediaType, setCurrentMediaType] = useState<'image' | 'video'>('image');
		const [currentMediaFileName, setCurrentMediaFileName] = useState<string>('');
		const [imageZoom, setImageZoom] = useState<number>(1);
		const [imagePosition, setImagePosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

		// Translation state
		const [translatedText, setTranslatedText] = useState<string | null>(null);
		const [selectedLanguage, setSelectedLanguage] = useState<string>('');
		const [showOriginal, setShowOriginal] = useState(false);

		// Hooks
		const { translateText } = useTranslate();
		const { data: languages } = useLanguages();
		const { t } = useTranslation();

	const isMessageEditable = useCallback(() => {
		const currentTimeSeconds = Math.floor(Date.now() / 1000);
		const messageTimeSeconds = Math.floor(message.createDate.getTime() / 1000);
		const timeDifferenceSeconds = currentTimeSeconds - messageTimeSeconds;
		const oneHourInSeconds = 3600;
		
		return timeDifferenceSeconds <= oneHourInSeconds;
	}, [message.createDate]);

		// Menu state helpers
		const open = Boolean(anchorEl);
		const contextMenuOpen = Boolean(contextMenuAnchorEl);
		const languageMenuOpen = Boolean(languageMenuAnchorEl);

		// Event handlers
		const handleMenuClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
			event.preventDefault();
			event.stopPropagation();
			setAnchorEl(event.currentTarget);
		}, []);

		const handleContextMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
			event.preventDefault();
			setContextMenuAnchorEl(event.currentTarget);
		}, []);

		const handleClose = useCallback(() => setAnchorEl(null), []);
		const handleContextMenuClose = useCallback(() => setContextMenuAnchorEl(null), []);
		const handleLanguageMenuClose = useCallback(() => setLanguageMenuAnchorEl(null), []);

		const handleEdit = useCallback(() => {
			handleClose();
			if (message.contents && message.contents.length > 0) {
				onEdit?.(message);
			}
		}, [handleClose, message, onEdit]);

		const handleDelete = useCallback(() => {
			handleClose();
			onDelete?.(message);
		}, [handleClose, message, onDelete]);

		// Media viewer handlers
		const openImageViewer = useCallback((imageUrl: string, fileName?: string) => {
			setCurrentMediaUrl(imageUrl);
			setCurrentMediaType('image');
			setCurrentMediaFileName(fileName || '');
			setImageZoom(1);
			setImagePosition({ x: 0, y: 0 });
			setMediaViewerOpen(true);
		}, []);

		const openVideoViewer = useCallback((videoUrl: string, fileName?: string) => {
			setCurrentMediaUrl(videoUrl);
			setCurrentMediaType('video');
			setCurrentMediaFileName(fileName || '');
			setMediaViewerOpen(true);
		}, []);

		const closeMediaViewer = useCallback(() => {
			setMediaViewerOpen(false);
			setImageZoom(1);
			setImagePosition({ x: 0, y: 0 });
		}, []);

		// Zoom handlers
		const handleZoomIn = useCallback(() => {
			setImageZoom((prev) => Math.min(prev + 0.5, 3));
		}, []);

		const handleZoomOut = useCallback(() => {
			setImageZoom((prev) => Math.max(prev - 0.5, 0.5));
		}, []);

		const handleZoomReset = useCallback(() => {
			setImageZoom(1);
			setImagePosition({ x: 0, y: 0 });
		}, []);

		const handleDownload = useCallback(() => {
			if (currentMediaUrl) {
				const link = document.createElement('a');
				link.href = currentMediaUrl;
				link.download = currentMediaFileName || 'image';
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			}
		}, [currentMediaUrl, currentMediaFileName]);

		// Translation handlers
		const handleTranslateOption = useCallback(
			(event: React.MouseEvent<HTMLElement>) => {
				setLanguageMenuAnchorEl(event.currentTarget);
				handleContextMenuClose();
			},
			[handleContextMenuClose]
		);

		const handleLanguageSelect = useCallback(
			async (langCode: string) => {
				setSelectedLanguage(langCode);
				handleLanguageMenuClose();
				setShowOriginal(false);

				const textToTranslate = message.contents
					?.filter(
						(content) =>
							content.type === MessageContentType.Text ||
							content.type === MessageContentType.RichText
					)
					.map((content) => content.content)
					.join('\n');

				if (textToTranslate) {
					try {
						const translationResults = await translateText({
							text: textToTranslate,
							targetLanguage: langCode,
						});

						if (translationResults?.[0]) {
							const cleanTranslatedText = translationResults[0].translatedText.replace(
								/<[^>]*>/g,
								''
							);
							setTranslatedText(cleanTranslatedText);
						}
					} catch (error) {
						console.error('Translation error:', error);
					}
				}
			},
			[handleLanguageMenuClose, message.contents, translateText]
		);

		const toggleOriginalTranslated = useCallback(() => {
			setShowOriginal(!showOriginal);
		}, [showOriginal]);

		// Content renderers
		const renderContent = useCallback(
			(content: MessageContent) => {
				switch (content.type) {
					case MessageContentType.Text:
						return (
							<Typography
								sx={{
									wordBreak: 'break-word',
									fontWeight: content.formatting?.bold ? 'bold' : 'normal',
									fontStyle: content.formatting?.italic ? 'italic' : 'normal',
									textDecoration:
										[
											content.formatting?.underline && 'underline',
											content.formatting?.strikethrough && 'line-through',
										]
											.filter(Boolean)
											.join(' ') || 'none',
									fontFamily:
										'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
								}}
							>
								{content.content}
							</Typography>
						);

					case MessageContentType.RichText:
						return <RichTextContent html={content.content} />;

					case MessageContentType.Image:
						return (
							<MediaImage
								src={getFullUrl(content.content)}
								alt="image"
								width={MEDIA_SIZES.image.width}
								height={MEDIA_SIZES.image.height}
								onClick={() =>
									openImageViewer(getFullUrl(content.content), content.metadata?.fileName)
								}
								fileName={content.metadata?.fileName}
								dimensions={content.metadata?.dimensions}
							/>
						);

					case MessageContentType.Video:
						return (
							<MediaVideo
								src={getFullUrl(content.content)}
								previewSrc={content.metadata?.preview ? getFullUrl(content.metadata.preview) : null}
								width={MEDIA_SIZES.video.width}
								height={MEDIA_SIZES.video.height}
								onClick={() =>
									openVideoViewer(getFullUrl(content.content), content.metadata?.fileName)
								}
								fileName={content.metadata?.fileName}
								duration={content.metadata?.duration}
							/>
						);

					case MessageContentType.Audio:
						return (
							<VoiceMessage
								src={getFullUrl(content.content)}
								isCurrentUser={isCurrentUser}
								fileName={content.metadata?.fileName}
							/>
						);

					case MessageContentType.File:
						return (
							<FileAttachment
								src={getFullUrl(content.content)}
								fileName={content.metadata?.fileName}
								fileSize={content.metadata?.fileSize}
								fileType={content.metadata?.mimeType}
							/>
						);

					case MessageContentType.Mention:
						return (
							<Typography
								component="span"
								sx={{
									color: 'primary.main',
									cursor: 'pointer',
									'&:hover': { textDecoration: 'underline' },
								}}
							>
								@{content.mentions?.[0]?.username}
							</Typography>
						);

					case MessageContentType.CodeBlock:
						return (
							<Box
								sx={{
									backgroundColor: '#f5f5f5',
									padding: 1,
									borderRadius: 1,
									fontFamily: 'monospace',
									overflowX: 'auto',
								}}
							>
								<pre style={{ margin: 0 }}>{content.content}</pre>
							</Box>
						);

					default:
						return <Typography>{content.content}</Typography>;
				}
			},
			[isCurrentUser, openImageViewer, openVideoViewer]
		);

		// Attachment renderer
		const renderAttachment = useCallback(
			(attachment: Attachment, index: number) => {
				if (!attachment?.s3_key) {
					console.warn('Invalid attachment:', attachment);
					return null;
				}

				const fullUrl = getFullUrl(attachment.s3_key);
				const key = `attachment-${index}`;
				const commonProps = {
					key,
					sx: { lineHeight: 1.4, mt: message.contents?.length ? 2 : 0 },
				};

				if (isVoiceMessage(attachment)) {
					return (
						<Box {...commonProps}>
							<VoiceMessage
								src={fullUrl}
								isCurrentUser={isCurrentUser}
								fileName={attachment.fileName}
							/>
						</Box>
					);
				}

				if (attachment.fileType === 'image' || attachment.fileType?.startsWith('image/')) {
					return (
						<Box {...commonProps}>
							<MediaImage
								src={fullUrl}
								alt="image"
								width={MEDIA_SIZES.image.width}
								height={MEDIA_SIZES.image.height}
								onClick={() => openImageViewer(fullUrl, attachment.fileName)}
								fileName={attachment.fileName}
							/>
						</Box>
					);
				}

				if (attachment.fileType === 'video' || attachment.fileType?.startsWith('video/')) {
					return (
						<Box {...commonProps}>
							<MediaVideo
								src={fullUrl}
								previewSrc={attachment.thumbnailKey ? getFullUrl(attachment.thumbnailKey) : null}
								width={MEDIA_SIZES.video.width}
								height={MEDIA_SIZES.video.height}
								onClick={() => openVideoViewer(fullUrl, attachment.fileName)}
								fileName={attachment.fileName}
							/>
						</Box>
					);
				}

				return (
					<Box {...commonProps}>
						<FileAttachment
							src={fullUrl}
							fileName={attachment.fileName}
							fileSize={undefined}
							fileType={attachment.fileType}
						/>
					</Box>
				);
			},
			[message.contents?.length, isCurrentUser, openImageViewer, openVideoViewer]
		);

		// Status indicator
		const renderStatusIndicator = useCallback(() => {
			if (!isCurrentUser) return null;

			switch (message.sendStatus) {
				case MessageSendStatus.Sending:
					return (
						<Box
							sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 0.5 }}
						>
							<Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
								{t('chats.sending')}
							</Typography>
							<Icon path={mdiClockOutline} size={0.6} color="#667781" />
						</Box>
					);
				case MessageSendStatus.Failed:
					return (
						<Box
							sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 0.5 }}
						>
							<Typography variant="caption" color="error" sx={{ mr: 1 }}>
								{t('chats.failedToSend')}
							</Typography>
							<Button
								variant="outlined"
								color="error"
								size="small"
								startIcon={<Icon path={mdiRefresh} size={0.7} />}
								onClick={() => {
									const messageWithRetry = message as MessageWithRetry;
									if (messageWithRetry.onRetry) {
										messageWithRetry.onRetry();
									} else if (onRetry) {
										onRetry(message);
									}
								}}
								sx={{
									py: 0,
									minHeight: '24px',
									fontSize: '0.7rem',
									textTransform: 'none',
								}}
							>
								{t('chats.retry')}
							</Button>
						</Box>
					);
				default:
					return null;
			}
		}, [isCurrentUser, message, onRetry, t]);

		// Early returns for special cases
		if (message.messageStatus === MessageStatus.Deleted) {
			return (
				<Box sx={{ padding: '2px 16px', display: 'flex', flexDirection: 'column' }}>
					<MessageBubbleComponent isCurrentUser={isCurrentUser}>
						<Typography sx={{ fontStyle: 'italic', color: '#667781' }}>
							{t('chats.messageDeleted')}
						</Typography>
					</MessageBubbleComponent>
				</Box>
			);
		}

		const hasNoContents = !message.contents || message.contents.length === 0;
		const hasNoAttachments = !message.attachments || message.attachments.length === 0;

		if (hasNoContents && hasNoAttachments) {
			return (
				<Box sx={{ padding: '2px 16px', display: 'flex', flexDirection: 'column' }}>
					<MessageBubbleComponent isCurrentUser={isCurrentUser}>
						<Typography sx={{ fontStyle: 'italic', color: '#667781' }}>Empty message</Typography>
					</MessageBubbleComponent>
				</Box>
			);
		}

		return (
			<Box sx={{ padding: '2px 16px', display: 'flex', flexDirection: 'column' }}>
				<MessageBubbleComponent isCurrentUser={isCurrentUser} onContextMenu={handleContextMenu}>
					{/* Text content */}
					{message.contents && message.contents.length > 0 && (
						<>
							{(!translatedText || showOriginal) &&
								message.contents.map((content, index) => (
									<Box key={index} sx={{ lineHeight: 1.4 }}>
										{renderContent(content)}
									</Box>
								))}
						</>
					)}

					{/* Translated text */}
					{translatedText && (
						<Box sx={{ position: 'relative' }}>
							{!showOriginal && <Typography variant="body2">{translatedText}</Typography>}
							<Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
								<Button
									size="small"
									onClick={toggleOriginalTranslated}
									sx={{
										minWidth: 'auto',
										p: '2px 4px',
										fontSize: '0.75rem',
										color: '#128C7E',
										textTransform: 'none',
									}}
								>
									{showOriginal ? t('chats.showTranslation') : t('chats.showOriginal')}
								</Button>
							</Box>
						</Box>
					)}

					{/* Attachments */}
					{message.attachments?.map(renderAttachment)}

					{/* Message footer */}
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							mt: 0.5,
							opacity: 0.7,
							'&:hover': { opacity: 1 },
						}}
					>
						{message.messageStatus === MessageStatus.Edited && (
							<Typography sx={{ fontSize: '0.75rem', color: '#667781', mr: 1 }}>
								{t('chats.edited')}
							</Typography>
						)}

						<TimeStamp>{formatTimestamp(message.createDate.getTime())}</TimeStamp>

						{/* Status indicators */}
						{isCurrentUser && message.sendStatus === MessageSendStatus.Sent && (
							<Typography
								variant="caption"
								color="success.main"
								sx={{ ml: 1, fontSize: '0.65rem' }}
							>
								✓
							</Typography>
						)}

						{isCurrentUser && message.sendStatus === MessageSendStatus.Sending && (
							<Box sx={{ ml: 1 }}>
								<Icon path={mdiClockOutline} size={0.6} color="#667781" />
							</Box>
						)}

						{/* Menu button */}
						{isCurrentUser && (
							<>
								<IconButton size="small" onClick={handleMenuClick} sx={{ ml: 1 }}>
									<Icon path={mdiDotsVertical} size={0.6} />
								</IconButton>
								<Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
									{message.contents && message.contents.length > 0 && isMessageEditable() && (
										<MenuItem onClick={handleEdit}>{t('chats.editMessage')}</MenuItem>
									)}
									<MenuItem onClick={handleDelete}>{t('chats.deleteMessage')}</MenuItem>
								</Menu>
							</>
						)}
					</Box>

					{renderStatusIndicator()}
				</MessageBubbleComponent>

				{/* Context menu */}
				<Menu
					anchorEl={contextMenuAnchorEl}
					open={contextMenuOpen}
					onClose={handleContextMenuClose}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
					transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				>
					{!isCurrentUser && (
						<MenuItem onClick={handleTranslateOption}>
							<Box sx={{ display: 'flex', alignItems: 'center' }}>
								<Icon path={mdiTranslate} size={0.7} style={{ marginRight: '8px' }} />
								{t('chats.translate')}
							</Box>
						</MenuItem>
					)}
					{isCurrentUser && (
						<>
							{message.contents && message.contents.length > 0 && isMessageEditable() && (
								<MenuItem onClick={handleEdit}>{t('chats.editMessage')}</MenuItem>
							)}
							<MenuItem onClick={handleDelete}>{t('chats.deleteMessage')}</MenuItem>
						</>
					)}
				</Menu>

				{/* Language selection menu */}
				<Menu
					anchorEl={languageMenuAnchorEl}
					open={languageMenuOpen}
					onClose={handleLanguageMenuClose}
					anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
					transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
					PaperProps={{ style: { maxHeight: 300 } }}
				>
					{languages?.map((lang) => (
						<MenuItem
							key={lang.code}
							onClick={() => handleLanguageSelect(lang.code)}
							selected={lang.code === selectedLanguage}
						>
							{lang.name}
						</MenuItem>
					))}
				</Menu>

				{/* Enhanced Media viewer modal */}
				<Modal
					open={mediaViewerOpen}
					onClose={closeMediaViewer}
					closeAfterTransition
					BackdropComponent={Backdrop}
					BackdropProps={{
						timeout: 500,
						sx: {
							backgroundColor: 'rgba(0, 0, 0, 0.9)',
							backdropFilter: 'blur(4px)',
						},
					}}
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						p: 2,
					}}
				>
					<Fade in={mediaViewerOpen}>
						<Box
							sx={{
								position: 'relative',
								maxWidth: '95vw',
								maxHeight: '95vh',
								outline: 'none',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
							}}
						>
							{/* Header with filename and controls */}
							{currentMediaFileName && (
								<Box
									sx={{
										position: 'absolute',
										top: -60,
										left: 0,
										right: 0,
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'space-between',
										color: 'white',
										backgroundColor: 'rgba(0, 0, 0, 0.7)',
										borderRadius: '8px',
										px: 2,
										py: 1,
										mb: 2,
										zIndex: 2,
									}}
								>
									<Typography
										variant="subtitle1"
										sx={{
											fontWeight: 500,
											maxWidth: '60%',
											overflow: 'hidden',
											textOverflow: 'ellipsis',
											whiteSpace: 'nowrap',
										}}
									>
										{currentMediaFileName}
									</Typography>

									{/* Image controls */}
									{currentMediaType === 'image' && (
										<Box sx={{ display: 'flex', gap: 1 }}>
											<Tooltip title="Zoom Out">
												<IconButton
													onClick={handleZoomOut}
													disabled={imageZoom <= 0.5}
													sx={{
														color: 'white',
														bgcolor: 'rgba(255,255,255,0.1)',
														'&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
														'&:disabled': { opacity: 0.3 },
													}}
													size="small"
												>
													<Icon path={mdiMagnifyMinus} size={0.8} />
												</IconButton>
											</Tooltip>

											<Tooltip title="Reset Zoom">
												<IconButton
													onClick={handleZoomReset}
													sx={{
														color: 'white',
														bgcolor: 'rgba(255,255,255,0.1)',
														'&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
													}}
													size="small"
												>
													<Typography sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
														{Math.round(imageZoom * 100)}%
													</Typography>
												</IconButton>
											</Tooltip>

											<Tooltip title="Zoom In">
												<IconButton
													onClick={handleZoomIn}
													disabled={imageZoom >= 3}
													sx={{
														color: 'white',
														bgcolor: 'rgba(255,255,255,0.1)',
														'&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
														'&:disabled': { opacity: 0.3 },
													}}
													size="small"
												>
													<Icon path={mdiMagnifyPlus} size={0.8} />
												</IconButton>
											</Tooltip>

											<Tooltip title="Download">
												<IconButton
													onClick={handleDownload}
													sx={{
														color: 'white',
														bgcolor: 'rgba(255,255,255,0.1)',
														'&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
													}}
													size="small"
												>
													<Icon path={mdiDownload} size={0.8} />
												</IconButton>
											</Tooltip>
										</Box>
									)}
								</Box>
							)}

							{/* Close button */}
							<Tooltip title="Close">
								<IconButton
									onClick={closeMediaViewer}
									sx={{
										position: 'absolute',
										top: currentMediaFileName ? -60 : -50,
										right: 0,
										color: 'white',
										bgcolor: 'rgba(0,0,0,0.7)',
										'&:hover': { bgcolor: 'rgba(0,0,0,0.9)' },
										zIndex: 3,
									}}
								>
									<Icon path={mdiClose} size={1} />
								</IconButton>
							</Tooltip>

							{/* Media content */}
							<Box
								sx={{
									position: 'relative',
									maxWidth: '100%',
									maxHeight: '100%',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									overflow: 'hidden',
									borderRadius: '12px',
									boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
								}}
							>
								{currentMediaType === 'image' ? (
									<img
										src={currentMediaUrl}
										alt="enlarged view"
										style={{
											maxWidth: imageZoom === 1 ? '90vw' : 'none',
											maxHeight: imageZoom === 1 ? '85vh' : 'none',
											width: imageZoom === 1 ? 'auto' : `${imageZoom * 100}%`,
											height: imageZoom === 1 ? 'auto' : 'auto',
											objectFit: 'contain',
											borderRadius: '12px',
											backgroundColor: 'white',
											cursor: imageZoom > 1 ? 'move' : 'default',
											transition: 'transform 0.2s ease-in-out',
											transform: `scale(${imageZoom}) translate(${imagePosition.x}px, ${imagePosition.y}px)`,
										}}
										draggable={false}
									/>
								) : (
									<video
										src={currentMediaUrl}
										controls
										autoPlay
										style={{
											maxWidth: '90vw',
											maxHeight: '85vh',
											borderRadius: '12px',
											backgroundColor: 'black',
										}}
									/>
								)}
							</Box>

							{/* Image info footer */}
							{/* {currentMediaType === 'image' && (
								<Box sx={{
									position: 'absolute',
									bottom: -50,
									left: 0,
									right: 0,
									color: 'white',
									backgroundColor: 'rgba(0, 0, 0, 0.7)',
									borderRadius: '8px',
									px: 2,
									py: 1,
									textAlign: 'center',
									zIndex: 2,
								}}>
									<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
										Click and drag to pan • Use zoom controls above • ESC to close
									</Typography>
								</Box>
							)} */}
						</Box>
					</Fade>
				</Modal>
			</Box>
		);
	}
);
